/* eslint-disable react-refresh/only-export-components */
import { useState, type ReactNode } from "react";
import { IN } from "./model";

export const Seg = <T extends string>({ options, value, onChange }: { options: [T, string][]; value: T; onChange: (k: T) => void }) => (
  <div className="seg">
    {options.map(([k, l]) => (
      <button key={k} type="button" aria-pressed={k === value} onClick={() => onChange(k)}>{l}</button>
    ))}
  </div>
);

export const Box = ({ title, sub, right, children, footer, style }: { title?: string; sub?: string; right?: ReactNode; children: ReactNode; footer?: ReactNode; style?: React.CSSProperties }) => (
  <div className="box" style={style}>
    {title && (
      <div className="box-h">
        <h3>{title}</h3>
        {sub && <span className="sub">{sub}</span>}
        {right && <div className="r">{right}</div>}
      </div>
    )}
    {children}
    {footer && <div className="box-f">{footer}</div>}
  </div>
);

export const Empty = ({ text }: { text: string }) => <p className="acap" style={{ padding: "18px" }}>{text}</p>;

export const Table = ({ head, rows, left = [], first = "t" }: { head: string[]; rows: (string | number | ReactNode)[][]; left?: number[]; first?: string }) => (
  <div className="tw">
    <table>
      <thead>
        <tr>{head.map((h, i) => <th key={h + i} className={left.includes(i) ? "l" : ""}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri}>{r.map((c, i) => <td key={i} className={i === 0 ? first : left.includes(i) ? "s l" : ""}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Spark = ({ vals }: { vals: (number | null)[] }) => {
  const v = vals.filter((x): x is number => x != null);
  if (v.length < 2) return null;
  const w = 60, h = 22, mn = Math.min(...v), mx = Math.max(...v);
  const pts = v.map((x, i) => [3 + (i * (w - 6)) / (v.length - 1), h - 3 - ((x - mn) / (mx - mn || 1)) * (h - 6)]);
  const last = pts[pts.length - 1];
  return (
    <svg className="spk" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline points={pts.map((p) => p.map((n) => n.toFixed(1)).join(",")).join(" ")} fill="none" stroke="#29276B" strokeOpacity=".55" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill="#29276B" />
    </svg>
  );
};

/** Season line chart, hover / click selects a point (ported from the design prototype) */
export const LineChart = ({ vals, labels, sel, onSel, fmt, tick, ref: refVal, refLabel, zero, aria }: {
  vals: (number | null)[]; labels: string[]; sel: number | null; onSel: (i: number) => void; fmt: (v: number) => string; tick: (v: number) => string | number;
  ref?: number | null; refLabel?: string; zero?: boolean; aria: string;
}) => {
  const W = 620, H = 220, pl = 44, pr = 16, pt = 26, pb = 30, n = vals.length;
  const vv = vals.filter((x): x is number => x != null).concat(refVal != null ? [refVal] : []);
  if (!vv.length) return null;
  let mn = zero ? 0 : Math.min(...vv), mx = Math.max(...vv);
  const pad = (mx - mn) * 0.15 || 1;
  if (!zero) mn -= pad;
  mx += pad;
  const step0 = (mx - mn) / 4, mag = Math.pow(10, Math.floor(Math.log10(step0 || 1)));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * mag).find((k) => k >= step0) ?? mag;
  mn = Math.floor(mn / step) * step; mx = mn + step * 4; if (zero) mn = Math.max(0, mn);
  const X = (i: number) => pl + (n === 1 ? (W - pl - pr) / 2 : (i * (W - pl - pr)) / (n - 1));
  const Y = (v: number) => pt + (1 - (v - mn) / (mx - mn)) * (H - pt - pb);
  const pts = vals.map((v, i) => (v == null ? null : [X(i), Y(v)] as [number, number]));
  const real = pts.filter(Boolean) as [number, number][];
  const d = real.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const cw = n > 1 ? (W - pl - pr) / (n - 1) : W;
  const txt = sel != null && vals[sel] != null ? fmt(vals[sel] as number) : "";
  const bw = Math.max(56, txt.length * 7.4 + 18);
  return (
    <svg className="tchart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={aria}>
      {[0, 1, 2, 3, 4].map((k) => { const v = mn + ((mx - mn) * k) / 4, y = Y(v); return (<g key={k}><line x1={pl} x2={W - pr} y1={y} y2={y} stroke="#EFF0F3" /><text x={pl - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9AA1AE">{tick(v)}</text></g>); })}
      {refVal != null && (<g><line x1={pl} x2={W - pr} y1={Y(refVal)} y2={Y(refVal)} stroke="#687080" strokeDasharray="5 5" /><text x={pl + 6} y={Y(refVal) - 6} fontSize="11" fontWeight="700" fill="#687080">{refLabel}</text></g>)}
      {real.length > 1 && (<g><path d={`${d} L${real[real.length - 1][0].toFixed(1)} ${H - pb} L${real[0][0].toFixed(1)} ${H - pb} Z`} fill="#29276B" fillOpacity=".07" /><path d={d} fill="none" stroke="#29276B" strokeWidth="2.5" strokeLinejoin="round" /></g>)}
      {sel != null && pts[sel] && <line x1={pts[sel]![0]} x2={pts[sel]![0]} y1={pts[sel]![1]} y2={H - pb} stroke="#29276B" strokeOpacity=".35" strokeDasharray="3 3" />}
      {pts.map((p, i) => p && (i === sel ? (<g key={i}><circle cx={p[0]} cy={p[1]} r="11" fill="#29276B" fillOpacity=".12" /><circle cx={p[0]} cy={p[1]} r="5.5" fill="#29276B" stroke="#fff" strokeWidth="2" /></g>) : (<circle key={i} cx={p[0]} cy={p[1]} r="4" fill="#fff" stroke="#29276B" strokeWidth="2" />)))}
      {labels.map((l, i) => <text key={i} x={X(i)} y={H - 8} textAnchor="middle" fontSize="11.5" fontWeight={i === sel ? 800 : 500} fill={i === sel ? "#15171C" : "#687080"}>{l}</text>)}
      {txt && pts[sel!] && (() => { const bx = Math.min(Math.max(pts[sel!]![0] - bw / 2, 2), W - bw - 2), by = Math.max(0, pts[sel!]![1] - 38); return (<g pointerEvents="none"><rect x={bx} y={by} width={bw} height="26" rx="6" fill="#15171C" /><text x={bx + bw / 2} y={by + 17} textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff">{txt}</text></g>); })()}
      {vals.map((v, i) => v != null && (<rect key={i} x={X(i) - cw / 2} y={0} width={cw} height={H} fill="transparent" tabIndex={0} role="button" aria-label={`${labels[i]}: ${fmt(v)}`} style={{ cursor: "pointer" }} onMouseEnter={() => onSel(i)} onClick={() => onSel(i)} onFocus={() => onSel(i)} />))}
    </svg>
  );
};

/** Recent-form bars: runs per match, wicket bubble, W/L/D tag */
export const FormChart = ({ items, avg }: { items: { runs: number; notOut: boolean; wk: number; res: "W" | "L" | "D"; opp: string; label: string }[]; avg: number | null }) => {
  const [sel, setSel] = useState<number | null>(null);
  const Wd = 640, H = 240, pl = 36, pr = 12, pt = 34, pb = 46;
  const slot = (Wd - pl - pr) / Math.max(items.length, 1), bw = Math.min(40, slot * 0.5);
  const mx = Math.ceil(Math.max(...items.map((m) => m.runs), 60) / 50) * 50;
  const Y = (v: number) => pt + (1 - v / mx) * (H - pt - pb), base = H - pb;
  const RC = { W: "#1C7C4E", L: "#B4382F", D: "#7A808C" };
  const cur = sel != null ? items[sel] : null;
  const tip = cur ? cur.label : "";
  const tw = Math.min(Wd - 8, tip.length * 6.3 + 20);
  const tx = cur ? Math.min(Math.max(pl + slot * (sel as number) + slot / 2 - tw / 2, 4), Wd - tw - 4) : 0;
  return (
    <svg className="tchart" viewBox={`0 0 ${Wd} ${H}`} role="img" aria-label="Runs and wickets in recent matches" onMouseLeave={() => setSel(null)}>
      {[0, 1, 2, 3].map((k) => { const v = (mx * k) / 3, y = Y(v); return (<g key={k}><line x1={pl} x2={Wd - pr} y1={y} y2={y} stroke="#EFF0F3" /><text x={pl - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#9AA1AE">{Math.round(v)}</text></g>); })}
      {avg != null && (<g><line x1={pl} x2={Wd - pr} y1={Y(avg)} y2={Y(avg)} stroke="#687080" strokeDasharray="5 5" /><text x={pl + 6} y={Y(avg) - 6} fontSize="11" fontWeight="700" fill="#687080" paintOrder="stroke" stroke="#fff" strokeWidth="3">Career avg {avg.toFixed(1)}</text></g>)}
      {items.map((m, i) => {
        const cx = pl + slot * i + slot / 2, y = Y(m.runs), on = sel === i, dim = sel != null && !on;
        return (
          <g key={i}>
            <rect x={cx - bw / 2} y={y} width={bw} height={Math.max(2, base - y)} rx="5" fill="#29276B" fillOpacity={dim ? 0.35 : avg != null && m.runs >= avg ? 1 : 0.55} />
            <text x={cx} y={y - 7} textAnchor="middle" fontSize="12" fontWeight="800" fill="#15171C">{m.runs}{m.notOut ? "*" : ""}</text>
            {m.wk > 0 && (<g><circle cx={cx + bw / 2 + 2} cy={y - 24} r="10" fill="#fff" stroke="#29276B" strokeWidth="2" /><text x={cx + bw / 2 + 2} y={y - 20} textAnchor="middle" fontSize="10" fontWeight="800" fill="#29276B">{m.wk}w</text></g>)}
            <rect x={cx - 9} y={base + 6} width="18" height="15" rx="3" fill={RC[m.res]} />
            <text x={cx} y={base + 17} textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#fff">{m.res}</text>
            <text x={cx} y={base + 36} textAnchor="middle" fontSize="10.5" fill={on ? "#15171C" : "#687080"} fontWeight={on ? 700 : 500}>{m.opp.split(" ")[0]}</text>
            <rect x={cx - slot / 2} y={0} width={slot} height={H} fill="transparent" tabIndex={0} role="button" aria-label={m.label} onMouseEnter={() => setSel(i)} onFocus={() => setSel(i)} />
          </g>
        );
      })}
      {cur && (<g pointerEvents="none"><rect x={tx} y={0} width={tw} height="24" rx="6" fill="#15171C" /><text x={tx + tw / 2} y={16} textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#fff">{tip}</text></g>)}
    </svg>
  );
};

/** 8-slice wagon wheel with an outer (deep) and inner ring, mirrored for left handers */
const SLICES = ["off-straight", "cover", "point", "third man", "fine leg", "square leg", "mid wicket", "on-straight"];
export const sectorOf = (name: string): { slice: number; outer: boolean } | null => {
  const n = name.toLowerCase().replace(/[-_]/g, " ");
  const outer = /^(deep|long)/.test(n);
  const pairs: [RegExp, number][] = [[/long off|mid off/, 0], [/cover/, 1], [/point/, 2], [/third man/, 3], [/fine leg/, 4], [/square leg/, 5], [/mid ?wicket/, 6], [/long on|mid on/, 7]];
  const hit = pairs.find(([re]) => re.test(n));
  return hit ? { slice: hit[1], outer } : null;
};
export const Wagon = ({ zones, leftHanded }: { zones: { name: string; runs: number }[]; leftHanded?: boolean }) => {
  const cx = 100, cy = 100, R = 92, cells = new Map<string, number>();
  zones.forEach((z) => { const s = sectorOf(z.name); if (!s) return; const k = `${s.slice}-${s.outer ? 1 : 0}`; cells.set(k, (cells.get(k) ?? 0) + z.runs); });
  const max = Math.max(1, ...cells.values());
  const paths: ReactNode[] = [];
  SLICES.forEach((nm, i) => {
    [0, 1].forEach((ring) => {
      const v = cells.get(`${i}-${ring}`) ?? 0;
      const idx = leftHanded ? (8 - i) % 8 : i;
      const a0 = ((-112.5 + idx * 45) * Math.PI) / 180, a1 = ((-67.5 + idx * 45) * Math.PI) / 180;
      const r0 = ring ? R * 0.55 : R * 0.12, r1 = ring ? R : R * 0.55;
      const pt = (r: number, a: number) => `${cx + r * Math.cos(a)} ${cy + r * Math.sin(a)}`;
      paths.push(<path key={`${i}-${ring}`} d={`M${pt(r0, a0)}L${pt(r1, a0)}A${r1} ${r1} 0 0 1 ${pt(r1, a1)}L${pt(r0, a1)}A${r0} ${r0} 0 0 0 ${pt(r0, a0)}Z`} fill="#29276B" fillOpacity={v ? 0.15 + (0.85 * v) / max : 0.04} stroke="#fff" strokeWidth="1"><title>{`${nm} ${ring ? "(deep)" : "(inner)"}: ${IN(v)} runs`}</title></path>);
    });
  });
  return (
    <svg viewBox="0 0 200 200" width="200" height="200" role="img" aria-label="Scoring zones">
      <circle cx="100" cy="100" r="92" fill="#F6F7F9" stroke="#E4E6EB" />
      {paths}
      <rect x="96" y="88" width="8" height="24" rx="2" fill="#fff" stroke="#9AA1AE" />
    </svg>
  );
};
